/**
 * The Files page's `Upload ▾` menu (Import/Export Flow V2 — OR-1400 /
 * OR-1351). One button, three ways in, grouped by whether data gets
 * extracted:
 *
 *   Auto-fill Orion
 *     • From Pricing Model    — the dedicated pricing-model path
 *     • From Broker Documents — Upstage extraction of ACORD / loss runs /
 *                               surveys into the CURRENT submission
 *   Upload without extracting the data
 *     • Attach Only           — plain file attach, no extraction
 *
 * Copy is verbatim from Andressa's approved Figma (aug-25, with JB).
 * Built on react-aria-components directly for the same reason as
 * `RowActionsMenu`: atlas-ui's Dropdown isn't exported from the barrel
 * and its Item can't do the two-line title + description shape.
 */
import type { FC, ReactNode, SVGProps } from "react";
import { ChevronDown, File02, Inbox01, Paperclip, Table } from "@untitledui/icons";
import {
    Button as AriaButton,
    Header as AriaHeader,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    MenuSection as AriaMenuSection,
    MenuTrigger as AriaMenuTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { cx } from "@tricura/atlas-ui";

export type UploadMenuAction = "pricing-model" | "broker-documents" | "attach-only" | "blank";

/** Which surface the menu sits on. Same shell, same two-group shape;
 *  the wording changes because Triage CREATES a submission and Files
 *  adds to the one already open, and the third option differs
 *  (blank submission vs. attach without extracting). */
export type UploadMenuVariant = "files" | "triage";

const TRIGGER_CLASS =
    // Match atlas's primary sm button (the design's solid lavender pill:
    // radius-full, 36px tall, spacing-lg/md padding — Figma Menu Button,
    // Files page).
    "bg-brand-solid hover:bg-brand-solid_hover text-white " +
    "focus-visible:ring-focus-ring inline-flex cursor-pointer items-center gap-1 rounded-full " +
    "px-3 py-2 text-sm font-semibold outline-none transition duration-100 ease-linear " +
    "focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50";

// Full-bleed menu (no inner padding): the section headers paint an
// edge-to-edge grey band and the items sit on white below them.
const MENU_CLASS = "bg-primary border-secondary w-80 overflow-hidden rounded-lg border shadow-lg outline-none";

const SECTION_HEADER_CLASS = "bg-secondary text-quaternary px-4 py-2 text-xs";

// Items keep a white gutter around them (mx/my) so the hover paints an
// INSET rounded card — the same grey as the headers — instead of a
// full-bleed strip. Outer margin + inner padding add up to the headers'
// 16px, so the text stays aligned across the whole menu.
const ITEM_CLASS =
    "group/item mx-2 my-1 flex cursor-pointer flex-col gap-1 rounded-lg px-2 py-3 outline-none " +
    "hover:bg-secondary focus:bg-secondary " +
    "data-disabled:cursor-not-allowed data-disabled:text-fg-disabled";

/** The Figma rules the auto-fill rows apart. Drawn as a bottom border on
 *  the item rather than a `Separator`, which react-aria only accepts as a
 *  direct `Menu` child, never inside a `MenuSection`. */
const ITEM_RULE_CLASS = "border-secondary border-b";

function ItemBody({ icon: Icon, title, description }: { icon: FC<SVGProps<SVGSVGElement>>; title: string; description: ReactNode }) {
    return (
        <>
            <span className="text-secondary flex items-center gap-2 text-sm font-semibold">
                <Icon aria-hidden="true" className="text-fg-quaternary size-4 shrink-0" />
                {title}
            </span>
            {/* Flush with the item's edge (not the title) per the Figma. */}
            <span className="text-tertiary text-xs font-normal">{description}</span>
        </>
    );
}

export function UploadMenu({
    onAction,
    variant = "files",
    isDisabled,
    pricingModelDisabled,
    className,
    "data-autotest": dataAutotest,
}: {
    onAction: (action: UploadMenuAction) => void;
    variant?: UploadMenuVariant;
    /** True while an upload/extraction is in flight — the design disables
     *  every entry point until it finishes (one upload across the product). */
    isDisabled?: boolean;
    /** From Pricing Model ships with OR-1351's import; disabled until then. */
    pricingModelDisabled?: boolean;
    className?: string;
    /** Forwarded to the trigger — the element a test actually clicks. */
    "data-autotest"?: string;
}) {
    const isTriage = variant === "triage";
    const label = isTriage ? "Create a Submission" : "Upload";
    return (
        <AriaMenuTrigger>
            <AriaButton
                aria-label={label}
                isDisabled={isDisabled}
                data-autotest={dataAutotest}
                className={cx(TRIGGER_CLASS, className)}
            >
                {label}
                <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
            </AriaButton>
            <AriaPopover placement="bottom end" offset={6}>
                <AriaMenu aria-label={label} className={MENU_CLASS} onAction={(key) => onAction(key as UploadMenuAction)}>
                    <AriaMenuSection>
                        <AriaHeader className={SECTION_HEADER_CLASS}>Auto-fill Orion</AriaHeader>
                        <AriaMenuItem
                            id="pricing-model"
                            textValue="From Pricing Model"
                            isDisabled={pricingModelDisabled}
                            data-autotest={dataAutotest ? `${dataAutotest}-pricing-model-item` : undefined}
                            className={cx(ITEM_CLASS, ITEM_RULE_CLASS)}
                        >
                            <ItemBody
                                icon={Table}
                                title="From Pricing Model"
                                description={
                                    isTriage ? (
                                        <>
                                            <span className="font-semibold">Creates a new submission</span> and auto-fills Orion using the
                                            pricing model's data.
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-semibold">Auto-fills Orion</span> from the pricing model, overwrites data
                                            already entered.
                                        </>
                                    )
                                }
                            />
                        </AriaMenuItem>
                        <AriaMenuItem
                            id="broker-documents"
                            textValue="From Broker Documents"
                            data-autotest={dataAutotest ? `${dataAutotest}-broker-documents-item` : undefined}
                            className={cx(ITEM_CLASS, ITEM_RULE_CLASS)}
                        >
                            <ItemBody
                                icon={Inbox01}
                                title="From Broker Documents"
                                description={
                                    isTriage ? (
                                        <>
                                            <span className="font-semibold">Creates a new submission</span> and auto-fills Orion using data
                                            from documents such as ACORD, loss runs, surveys and more.
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-semibold">Auto-fills Orion</span> with data from documents like ACORD, loss
                                            runs, surveys, and more. Only the fields covered by those documents are updated.
                                        </>
                                    )
                                }
                            />
                        </AriaMenuItem>
                    </AriaMenuSection>
                    {/* The second group is where the two surfaces diverge:
                      * Triage's third path starts an empty submission, Files'
                      * keeps a document without reading it. */}
                    <AriaMenuSection>
                        <AriaHeader className={SECTION_HEADER_CLASS}>
                            {isTriage ? "Create a blank submission" : "Upload without extracting the data"}
                        </AriaHeader>
                        {isTriage ? (
                            <AriaMenuItem
                                id="blank"
                                textValue="Blank Submission"
                                data-autotest={dataAutotest ? `${dataAutotest}-blank-item` : undefined}
                                className={ITEM_CLASS}
                            >
                                <ItemBody icon={File02} title="Blank Submission" description="Create a submission from scratch." />
                            </AriaMenuItem>
                        ) : (
                            <AriaMenuItem
                                id="attach-only"
                                textValue="Attach Only"
                                data-autotest={dataAutotest ? `${dataAutotest}-attach-only-item` : undefined}
                                className={ITEM_CLASS}
                            >
                                <ItemBody
                                    icon={Paperclip}
                                    title="Attach Only"
                                    description={
                                        <>
                                            Attaches the file to the submission.{" "}
                                            <span className="font-semibold">No data is extracted</span>.
                                        </>
                                    }
                                />
                            </AriaMenuItem>
                        )}
                    </AriaMenuSection>
                </AriaMenu>
            </AriaPopover>
        </AriaMenuTrigger>
    );
}
